(() => {
	if (window.customBisListenersInstalled) return;
	window.customBisListenersInstalled = true;
	const states = new WeakMap();
	const initialized = new WeakSet();

	function syncSelection(root) {
		const select = root.querySelector('[data-bis-variant]');
		if (root.dataset.submittedVariant && root.dataset.submittedVariant !== select.value) {
			root.querySelector('.custom-bis__form').hidden = false;
			root.querySelector('.custom-bis__note').hidden = true;
			root.querySelector('.custom-bis__sms-form').hidden = true;
			const title = root.querySelector('[data-email-title]');
			if (title) title.hidden = false;
			delete root.dataset.submittedVariant;
		}
		root.dataset.variantId = select.value;
		root.querySelector('[data-status]').textContent = select.value
			? '' : 'There are no sold-out options for this product.';
		select.disabled = !select.options.length;
		root.querySelector('button[type="submit"]').disabled =
			!select.value || Boolean(states.get(root)?.pending);
	}

	async function initialize(root) {
		if (initialized.has(root)) return;
		initialized.add(root);
		syncSelection(root);
		if (Number(root.dataset.variantCount) <= 250) return;
		const select = root.querySelector('[data-bis-variant]');
		const status = root.querySelector('[data-status]');
		status.textContent = 'Loading all sold-out options…';
		try {
			const variants = [];
			let after = null;
			do {
	const response = await fetch('/api/2026-07/graphql.json', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			query: `query BisVariants($id: ID!, $after: String, $country: CountryCode)
				@inContext(country: $country) {
				product(id: $id) { variants(first: 100, after: $after) {
		nodes { id title availableForSale }
		pageInfo { hasNextPage endCursor }
				} }
			}`,
			variables: { id: 'gid://shopify/Product/' + root.dataset.productId,
				after, country: root.dataset.country || null }
		})
	});
	const result = await response.json();
	const page = result.data?.product?.variants;
	if (!response.ok || result.errors?.length || !page) throw new Error();
	variants.push(...page.nodes);
	after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
	if (page.pageInfo.hasNextPage && !after) throw new Error();
			} while (after);
			const selected = select.value;
			select.replaceChildren(...variants.filter(v => !v.availableForSale).map(v =>
	new Option(v.title, v.id.split('/').pop())
			));
			if (Array.from(select.options).some(option => option.value === selected)) {
	select.value = selected;
			}
			syncSelection(root);
		} catch {
			status.textContent = 'Could not load all sold-out options. Refresh to try again.';
		}
	}

	function initializeAll() {
		document.querySelectorAll('[data-custom-bis]').forEach(initialize);
	}
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
	} else initializeAll();
	document.addEventListener('shopify:section:load', initializeAll);
	new MutationObserver(initializeAll).observe(document.documentElement, {
		childList: true, subtree: true
	});

	document.addEventListener('change', (event) => {
		if (event.target.matches('[data-custom-bis] input[name="sms_marketing"]')) {
			const disclaimer = event.target.closest('.custom-bis__sms-form').querySelector('[data-sms-disclaimer]');
			if (disclaimer) disclaimer.hidden = !event.target.checked;
			return;
		}
		if (!event.target.matches('[data-bis-variant]')) return;
		syncSelection(event.target.closest('[data-custom-bis]'));
	});

	document.addEventListener('variant:changed', (event) => {
		const targetId = event.target?.getAttribute?.('id') || '';
		document.querySelectorAll('[data-custom-bis]').forEach((root) => {
			const formId = root.dataset.productForm;
			const matches = formId
	? targetId === formId
	: targetId.startsWith('product-form-') &&
		targetId.endsWith('-' + root.dataset.productId);
			if (!matches) return;
			const variant = event.detail?.variant;
			if (!variant) return;
			const select = root.querySelector('[data-bis-variant]');
			const option = Array.from(select.options).find(o => o.value === String(variant.id));
			if (!variant.available) {
	if (!option) select.add(new Option(variant.title, String(variant.id)));
	select.value = String(variant.id);
			} else if (option) option.remove();
			syncSelection(root);
		});
	});

	function stateFor(root) {
		if (!states.has(root)) states.set(root, { pending: false, emailAlerts: new Set(), consents: new Set() });
		return states.get(root);
	}

	async function post(root, endpoint, data) {
		if (!root.dataset.companyId) throw new Error('The notification form is not configured.');
		const response = await fetch(
			'https://a.klaviyo.com/client/' + endpoint + '/?company_id=' + encodeURIComponent(root.dataset.companyId),
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
				body: JSON.stringify({ data })
			}
		);
		if (response.status !== 202) throw new Error(response.status === 429
			? 'Too many attempts. Please wait a moment and try again.'
			: 'Unable to save your request. Please check your details and try again.');
	}

	function stockAlert(root, profile, variantId, channel) {
		return post(root, 'back-in-stock-subscriptions', {
			type: 'back-in-stock-subscription',
			attributes: { profile: { data: { type: 'profile', attributes: profile } }, channels: [channel] },
			relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } }
		});
	}

	async function marketing(root, profile, channel) {
		const listId = (root.dataset[channel + 'ListId'] || '').trim();
		if (channel === 'email' && listId && listId === (root.dataset.smsListId || '').trim()) {
			throw new Error('Email marketing signup is currently unavailable: the email and SMS lists must be different.');
		}
		if (!listId) throw new Error(channel === 'sms'
			? 'Text signup is currently unavailable.' : 'Email marketing signup is currently unavailable.');
		const key = JSON.stringify([profile, channel, listId]);
		const state = stateFor(root);
		if (state.consents.has(key)) return;
		await post(root, 'subscriptions', {
			type: 'subscription',
			attributes: {
				custom_source: 'Back in Stock',
				profile: { data: { type: 'profile', attributes: {
					...profile,
					subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } }
				} } }
			},
			relationships: { list: { data: { type: 'list', id: listId } } }
		});
		state.consents.add(key);
	}

	function finish(root) {
		root.querySelector('.custom-bis__sms-form').hidden = true;
		const note = root.querySelector('.custom-bis__note');
		note.hidden = false;
		note.focus();
	}

	document.addEventListener('click', (event) => {
		const skip = event.target.closest('[data-bis-skip]');
		if (!skip) return;
		const root = skip.closest('[data-custom-bis]');
		if (!root || stateFor(root).pending || root.querySelector('.custom-bis__sms-form').hidden) return;
		finish(root);
	});

	document.addEventListener('input', (event) => {
		if (event.target.matches('[data-custom-bis] input[name="phone_number"]')) {
			event.target.setCustomValidity('');
		}
	});

	document.addEventListener('submit', async (event) => {
		const form = event.target;
		const root = form.closest('[data-custom-bis]');
		if (!root || !form.matches('.custom-bis__form, .custom-bis__sms-form')) return;
		event.preventDefault();
		const state = stateFor(root);
		if (state.pending || form.hidden || root.hidden) return;
		const sms = form.matches('.custom-bis__sms-form');
		const status = form.querySelector(sms ? '[data-sms-status]' : '[data-status]');
		status.textContent = '';
		let phone;
		if (sms) {
			phone = form.elements.phone_number.value.trim().replace(/[\s().-]/g, '');
			form.elements.phone_number.setCustomValidity(/^\d{10}$/.test(phone)
				? '' : 'Enter your 10-digit phone number, including area code, for example 555 555 0123.');
			phone = '+1' + phone;
		}
		if (!form.reportValidity()) return;
		if (sms && !form.elements.sms_marketing.checked) return;
		const variantId = sms ? state.variantId : root.querySelector('[data-bis-variant]').value;
		if (!variantId) return;
		const email = sms ? state.email : form.elements.email.value.trim();
		const emailConsent = !sms && form.elements.email_marketing.checked;
		const controls = Array.from(form.querySelectorAll('input, select, button'));
		const disabled = controls.map(control => control.disabled);
		const button = form.querySelector('button[type="submit"]');
		const buttonText = button.textContent;
		state.pending = true;
		controls.forEach(control => { control.disabled = true; });
		button.textContent = 'Submitting…';
		try {
			if (sms) {
				await marketing(root, { email, phone_number: phone }, 'sms');
				await stockAlert(root, { email, phone_number: phone }, variantId, 'SMS');
				if (root.dataset.variantId === variantId) finish(root);
			} else {
				const key = JSON.stringify([email, variantId]);
				if (!state.emailAlerts.has(key)) {
					await stockAlert(root, { email }, variantId, 'EMAIL');
					state.emailAlerts.add(key);
				}
				let marketingResult = '';
				if (emailConsent) {
					try {
						await marketing(root, { email }, 'email');
						marketingResult = '';
					} catch (error) {
						marketingResult = 'Your stock alert is saved, but email marketing signup failed. ' + error.message;
					}
				}
				if (root.dataset.variantId !== variantId) return;
				state.email = email;
				state.variantId = variantId;
				root.dataset.submittedVariant = variantId;
				form.hidden = true;
				const title = root.querySelector('[data-email-title]');
				if (title) title.hidden = true;
				const smsForm = root.querySelector('.custom-bis__sms-form');
				smsForm.reset();
				const disclaimer = smsForm.querySelector('[data-sms-disclaimer]');
				if (disclaimer) disclaimer.hidden = true;
				smsForm.elements.phone_number.setCustomValidity('');
				smsForm.querySelector('[data-sms-status]').textContent = '';
				smsForm.querySelector('[data-email-result]').textContent = marketingResult;
				smsForm.hidden = false;
				smsForm.querySelector('h1').focus();
			}
		} catch (error) {
			if (root.dataset.variantId === variantId) status.textContent =
				(error instanceof TypeError ? 'Connection problem. Please try again.' : error.message) +
				(sms ? ' Your email stock alert is still saved.' : '');
		} finally {
			state.pending = false;
			controls.forEach((control, index) => { control.disabled = disabled[index]; });
			button.textContent = buttonText;
			root.querySelector('.custom-bis__form button[type="submit"]').disabled =
				!root.querySelector('[data-bis-variant]').value;
		}
	});
})();
